/**
 * useAIRefDrop
 *
 * Handles dropping a product asset onto an AI reference slot.
 * Used by the ProductImagesSubTab orchestrator to intercept
 * AI-ref drops before the default DnD handler runs.
 */

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { setAIReferenceImage } from '@/lib/api/products';

export function useAIRefDrop(productId: string, defaultRole?: string) {
  const queryClient = useQueryClient();
  const [savingSlot, setSavingSlot] = useState<number | null>(null);

  const handleAIRefDrop = useCallback(
    async (slot: number, assetId: string) => {
      setSavingSlot(slot);
      try {
        // Preserve existing role from cache if replacing
        const cached = queryClient.getQueryData<{ success: boolean; slots: { slot: number; role?: string }[] }>(
          ['ai-ref-slots', productId],
        );
        const existingRole = cached?.slots?.find((s) => s.slot === slot)?.role;

        const res = await setAIReferenceImage(productId, {
          slot,
          asset_id: assetId,
          role: existingRole || defaultRole,
        });
        if (res.success) {
          await queryClient.invalidateQueries({ queryKey: ['ai-ref-slots', productId] });
          toast.success('KI-Referenzbild gesetzt');
        }
      } catch {
        toast.error('Fehler beim Setzen des Referenzbildes');
      } finally {
        setSavingSlot(null);
      }
    },
    [productId, queryClient, defaultRole],
  );

  return { savingSlot, handleAIRefDrop };
}
